import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Horizontal draggable carousel of uniform-sized images.
 * - Uniform aspect ratio (16:10) and fixed responsive width.
 * - Drag-to-scroll on desktop, native scroll on touch.
 * - Optional prev/next arrows on desktop.
 */
export default function BlogGallery({ images = [], onOpen }) {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });
  const [showArrows, setShowArrows] = useState(false);

  if (!images.length) return null;

  const onDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = {
      active: true,
      startX: e.pageX - el.offsetLeft,
      startScroll: el.scrollLeft,
      moved: false,
    };
    el.classList.add("dragging");
  };
  const onMove = (e) => {
    if (!drag.current.active) return;
    const el = ref.current;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - drag.current.startX;
    if (Math.abs(walk) > 5) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - walk;
  };
  const stopDrag = () => {
    drag.current.active = false;
    ref.current?.classList.remove("dragging");
  };
  const onClickItem = (src) => {
    if (drag.current.moved) return; // suppress click after drag
    onOpen?.(src);
  };

  const scrollByCards = (dir) => {
    const el = ref.current;
    if (!el) return;
    const card = el.querySelector("[data-carousel-item]");
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.9;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div
      className="relative"
      data-testid="blog-gallery-carousel"
      onMouseEnter={() => setShowArrows(true)}
      onMouseLeave={() => setShowArrows(false)}
    >
      <div
        ref={ref}
        className="h-scroll cursor-grab"
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
      >
        {images.map((src, i) => (
          <button
            key={i}
            type="button"
            data-carousel-item
            data-testid={`blog-gallery-item-${i}`}
            onClick={() => onClickItem(src)}
            className="card-rotta img-zoom overflow-hidden aspect-[16/10] w-[85%] sm:w-[520px] lg:w-[600px] p-0 border-[#1f1f1f]"
          >
            <img src={src} alt="" draggable={false} className="w-full h-full object-cover select-none pointer-events-none" loading="lazy" />
          </button>
        ))}
      </div>

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Anterior"
            data-testid="blog-gallery-prev"
            onClick={() => scrollByCards(-1)}
            className={`hidden md:grid absolute top-1/2 -translate-y-1/2 left-2 w-10 h-10 place-items-center rounded-full bg-[#0B0B0B]/80 border border-[#C6A762]/40 text-[#C6A762] hover:bg-[#C6A762] hover:text-[#0B0B0B] transition-all ${showArrows ? "opacity-100" : "opacity-0"}`}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Próximo"
            data-testid="blog-gallery-next"
            onClick={() => scrollByCards(1)}
            className={`hidden md:grid absolute top-1/2 -translate-y-1/2 right-2 w-10 h-10 place-items-center rounded-full bg-[#0B0B0B]/80 border border-[#C6A762]/40 text-[#C6A762] hover:bg-[#C6A762] hover:text-[#0B0B0B] transition-all ${showArrows ? "opacity-100" : "opacity-0"}`}
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}
    </div>
  );
}
